import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { type SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  CHART_NOTES_STYLE_TAB_LIST_SCROLL_SX,
  CHART_NOTES_STYLE_TAB_LIST_SX,
  ChartNotesStyleTabLabel
} from "components/chart-notes-style-tabs";
import ErrorSnackbarContent from "components/error-snackbar-content";
import {
  AMBIENT_SCRIBE_CUSTOM_TEMPLATES,
  AMBIENT_SCRIBE_PAUSED_FEATURE,
  AMBIENT_SCRIBE_PROCESSING_EVENTS,
  PAYWALL_BYPASS
} from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { showFeatureDialog } from "features/auth/auth-slice";
import MedicalScribeFeedbackDialog from "features/medical-scribe/components/medical-scribe-feedback-dialog.component";
import {
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  AMBIENT_SCRIBE_STATUS_RECORDING,
  AMBIENT_SCRIBE_STATUS_RETRYING,
  AMBIENT_SCRIBE_STATUS_TRANSCRIBED,
  AMBIENT_SCRIBE_STATUS_UPLOADED,
  RECORDING_STATE_IN_PROGRESS,
  getScribeModalNestedDialogZIndex
} from "features/medical-scribe/medical-scribe-constants";
import { getChunkById } from "features/medical-scribe/medical-scribe-db/medical-scribe-db.util";
import { selectMedicalScribe } from "features/medical-scribe/medical-scribe.slice";
import type { MedicalScribe } from "features/medical-scribe/medical-scribes-section/medical-scribes.type";
import ProcessingEventsTimeline from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/tabs/processing-events-timeline/processing-events-timeline.component";
import EditScribeTab from "features/medical-scribe/paused-edit";
import PausedScribeActionButtons from "features/medical-scribe/paused-edit/paused-scribe-action-buttons.component";
import PausedStatusWarningAlert from "features/medical-scribe/paused-edit/paused-status-warning-alert.component";
import ScribeContextEditor from "features/medical-scribe/paused-edit/scribe-context-editor.component";
import { usePausedScribeActions } from "features/medical-scribe/paused-edit/use-paused-scribe-actions.hook";
import { recordToDataUrl } from "features/medical-scribe/utils";
import { AMBIENT_SCRIBE_REFETCH } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import {
  DEFAULT_AMBIENT_SCRIBE_QUERY,
  DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED,
  DEFAULT_AMBIENT_SCRIBE_QUERY_WITH_TEMPLATE,
  useGenerateAmbientSoapNotesMutation,
  useGenerateAmbientSummaryMutation,
  useGenerateTranscriptionMutation,
  useGetAmbientScribeQuery,
  useGetScribeAudioChunksQuery
} from "services/ambient-scribe";
import { useLazyGetCurrentUserPaywallFeaturesQuery } from "services/paywall";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { getFullName } from "utils/common";

import ScribeAudioPlayer from "./scribe-audio-player.component";
import ScribeMetadataHeader from "./scribe-metadata-header.component";
import TextTab from "./tabs/text-tab.component";
import TranscriptTab from "./tabs/transcript-tab.component";

type GenerateError =
  | { kind: "subscription"; error: string; message: string }
  | { kind: "generic" };

const DETAIL_TAB_DISPLAY_ORDER = [
  "soap",
  "context",
  "transcript",
  "summary",
  "events"
] as const;

const DETAIL_TAB_LABELS: Record<
  (typeof DETAIL_TAB_DISPLAY_ORDER)[number],
  string
> = {
  soap: "Scribe",
  context: "Scribe Context",
  transcript: "Transcript",
  summary: "Summary",
  events: "Logs"
};

function getDetailTabLabel(
  tabId: (typeof DETAIL_TAB_DISPLAY_ORDER)[number],
  hasTranscription: boolean
): string {
  if (tabId === "context" && !hasTranscription) {
    return "Scribe Context";
  }
  return DETAIL_TAB_LABELS[tabId];
}

type DetailsMode = "view" | "edit";
type EditBottomTab = "sessions" | "context";

type ViewDetailsProps = {
  remoteMedicalScribeId: number | undefined;
  localMedicalScribe?: MedicalScribe;
  hideHeader?: boolean;
  detailsMode?: DetailsMode;
  onDetailsModeChange?: (mode: DetailsMode) => void;
  editBottomTab?: EditBottomTab;
  onEditBottomTabChange?: (tab: EditBottomTab) => void;
  /** Atomically sets edit sub-tab + Audio Recordings mode (used when parent owns both). */
  onEnterEdit?: (bottomTab: EditBottomTab) => void;
  onEditClose?: () => void;
  onEditFinalized?: () => void;
  onTabSelected?: (value: string) => void;
  onScribeUpdated?: (scribe: AmbientScribe) => void;
  sx?: SxProps;
};

export default function ViewScribeDetails({
  remoteMedicalScribeId,
  localMedicalScribe: local,
  hideHeader,
  detailsMode,
  onDetailsModeChange,
  editBottomTab: editBottomTabProp,
  onEditBottomTabChange,
  onEnterEdit,
  onEditClose,
  onEditFinalized,
  sx,
  onTabSelected,
  onScribeUpdated
}: ViewDetailsProps) {
  const theme = useTheme();
  const flags = useFlags();
  const isPausedFeatureEnabled = flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false;
  const isAmbientScribeCustomTemplatesEnabled = Boolean(
    flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES]
  );

  const isControlled = detailsMode !== undefined;
  const [internalMode, setInternalMode] = useState<DetailsMode>("view");
  const mode = isControlled ? detailsMode! : internalMode;

  const setMode = useCallback(
    (next: DetailsMode) => {
      if (isControlled) onDetailsModeChange?.(next);
      else setInternalMode(next);
    },
    [isControlled, onDetailsModeChange]
  );

  const prevRemoteIdRef = useRef(remoteMedicalScribeId);
  useEffect(() => {
    if (isControlled) return;
    if (prevRemoteIdRef.current !== remoteMedicalScribeId) {
      setInternalMode("view");
      prevRemoteIdRef.current = remoteMedicalScribeId;
    }
  }, [remoteMedicalScribeId, isControlled]);

  const showViewEditTabs =
    isPausedFeatureEnabled && remoteMedicalScribeId != null;

  const { data: audioChunks = [], isLoading: isAudioChunksLoading } =
    useGetScribeAudioChunksQuery(
      { scribeId: Number(remoteMedicalScribeId) },
      { skip: !showViewEditTabs || !remoteMedicalScribeId }
    );
  const audioChunksCount = audioChunks.length;

  const { userId } = useUserRoles();
  const isEditBottomTabControlled = editBottomTabProp !== undefined;
  const [internalEditBottomTab, setInternalEditBottomTab] =
    useState<EditBottomTab>("sessions");
  const editBottomTab = isEditBottomTabControlled
    ? editBottomTabProp!
    : internalEditBottomTab;
  const flushContextSaveRef = useRef<(() => Promise<void>) | null>(null);

  const setEditBottomTab = useCallback(
    (tab: EditBottomTab) => {
      if (isEditBottomTabControlled) {
        onEditBottomTabChange?.(tab);
      } else {
        setInternalEditBottomTab(tab);
      }
    },
    [isEditBottomTabControlled, onEditBottomTabChange]
  );

  useEffect(() => {
    setEditBottomTab("sessions");
  }, [remoteMedicalScribeId, setEditBottomTab]);

  const handleEditFinalized = useCallback(() => {
    setEditBottomTab("sessions");
    onEditFinalized?.();
    setMode("view");
  }, [onEditFinalized, setMode, setEditBottomTab]);

  const handleTopTabChange = useCallback(
    (_: unknown, v: DetailsMode) => {
      if (v === mode) return;
      if (v === "edit") {
        if (onEnterEdit) {
          onEnterEdit("sessions");
          return;
        }
        setEditBottomTab("sessions");
      }
      setMode(v);
    },
    [mode, setMode, setEditBottomTab, onEnterEdit]
  );

  const paywallBypassEnabled = flags[PAYWALL_BYPASS] ?? true;
  const ambientScribeProcessingEventsEnabled = Boolean(
    flags[AMBIENT_SCRIBE_PROCESSING_EVENTS]
  );

  const dispatch = useAppDispatch();
  const { recordingState } = useAppSelector(selectMedicalScribe);

  // ─── API mutations ────────────────────────────────────────────────────────
  const [generateSoapNotes] = useGenerateAmbientSoapNotesMutation();
  const [generateSummary] = useGenerateAmbientSummaryMutation();
  const [generateTranscription] = useGenerateTranscriptionMutation();
  const [fetchPaywallFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();

  // ─── Polling ─────────────────────────────────────────────────────────────
  const [scribePollingInterval, setScribePollingInterval] = useState(0);

  // Paused-edit query is a superset: includes chart_note + ambient_scribe_template_id.
  // chart_note is required for resumePausedScribe when the flag is on.
  const ambientScribeDetailQuery = isPausedFeatureEnabled
    ? DEFAULT_AMBIENT_SCRIBE_QUERY_PAUSED
    : isAmbientScribeCustomTemplatesEnabled
      ? DEFAULT_AMBIENT_SCRIBE_QUERY_WITH_TEMPLATE
      : DEFAULT_AMBIENT_SCRIBE_QUERY;

  const {
    data: remote,
    isLoading,
    isFetching,
    refetch: refetchScribe
  } = useGetAmbientScribeQuery(
    {
      id: Number(remoteMedicalScribeId),
      query: ambientScribeDetailQuery
    },
    {
      skip: !remoteMedicalScribeId,
      pollingInterval: scribePollingInterval,
      refetchOnMountOrArgChange: true
    }
  );

  useEventBus(AMBIENT_SCRIBE_REFETCH, () => {
    if (remoteMedicalScribeId) refetchScribe();
  });

  const showPausedScribeActionButtons =
    isPausedFeatureEnabled && Boolean(remoteMedicalScribeId);

  const isPausedStatus = remote?.status === AMBIENT_SCRIBE_STATUS_PAUSED;
  const isCompletedStatus = remote?.status === AMBIENT_SCRIBE_STATUS_COMPLETED;
  const isRecordingStatus = remote?.status === AMBIENT_SCRIBE_STATUS_RECORDING;
  const isProcessingStatus =
    remote?.status === AMBIENT_SCRIBE_STATUS_PROCESSING;

  const {
    handleResume: handlePausedResume,
    handleComplete: handlePausedComplete,
    isResuming: isPausedResuming,
    isResumePleaseWait: isPausedResumePleaseWait,
    isCompleting: isPausedCompleting,
    isWidgetActive: isPausedWidgetActive,
    isLocallyPausedWidget: isPausedLocallyPausedWidget,
    canCompleteFromFailedOrCancelled,
    isFailedOrCancelledAwaitingChunks
  } = usePausedScribeActions({
    scribeId: remoteMedicalScribeId,
    scribeStatus: remote?.status,
    patient: remote?.patient,
    chartNoteId: remote?.chart_note,
    onClose: onEditClose,
    onFinalized: handleEditFinalized,
    onBeforeAction: () => flushContextSaveRef.current?.() ?? Promise.resolve()
  });

  // ─── Summary generate error dialog ───────────────────────────────────────
  const [summaryGenerateError, setSummaryGenerateError] =
    useState<GenerateError | null>(null);

  // ─── Generation state ─────────────────────────────────────────────────────
  // Local bridge: set immediately on user click so UI transitions before the
  // next poll confirms is_generating_* on the server.
  const [localGenerating, setLocalGenerating] = useState({
    soap: false,
    summary: false,
    transcript: false
  });

  // Single truth per tab — union of local intent and server state
  const isGeneratingSoap =
    localGenerating.soap || remote?.is_generating_soap === true;
  const isGeneratingSummary =
    localGenerating.summary || remote?.is_generating_summary === true;
  const isGeneratingTranscript = localGenerating.transcript;

  // Polling starts as soon as localGenerating is true — no gap between click
  // and server confirmation.
  const shouldPoll =
    localGenerating.soap ||
    localGenerating.summary ||
    remote?.is_generating_soap === true ||
    remote?.is_generating_summary === true ||
    remote?.status === AMBIENT_SCRIBE_STATUS_PROCESSING ||
    isPausedCompleting;

  useEffect(() => {
    setScribePollingInterval(shouldPoll ? 2500 : 0);
  }, [shouldPoll]);

  // Clear localGenerating when content arrives from server or server confirms
  // generation is no longer in progress.
  useEffect(() => {
    setLocalGenerating((prev) => {
      const next = { ...prev };
      if (
        prev.soap &&
        (remote?.soap_notes || remote?.is_generating_soap === false)
      ) {
        next.soap = false;
      }
      if (
        prev.summary &&
        (remote?.summary || remote?.is_generating_summary === false)
      ) {
        next.summary = false;
      }
      if (prev.transcript && remote?.has_transcription) {
        next.transcript = false;
      }
      return next;
    });
  }, [
    remote?.soap_notes,
    remote?.summary,
    remote?.has_transcription,
    remote?.is_generating_soap,
    remote?.is_generating_summary
  ]);

  // Propagate polled remote data to parent so it can keep selectedScribe in sync.
  // This ensures the Insert button and inserted content reflect the latest state
  // after async operations like SOAP regeneration complete.
  useEffect(() => {
    if (remote) onScribeUpdated?.(remote);
  }, [remote, onScribeUpdated]);

  // ─── Audio URL ────────────────────────────────────────────────────────────
  // Remote and local-URL cases are derived directly from reactive RTK Query
  // state — no effect needed, no extra render cycle.
  // Only the IndexedDB path requires async state because it involves a
  // Promise-based read outside of React's data flow.
  const [localBlobUrl, setLocalBlobUrl] = useState<string | undefined>();
  const audioObjectUrlRef = useRef<string | undefined>();

  useEffect(() => {
    if (local?.local_id) {
      getChunkById(String(local.local_id)).then((result) => {
        if (result?.id) {
          const objectUrl = recordToDataUrl(result.chunks);
          audioObjectUrlRef.current = objectUrl;
          setLocalBlobUrl(objectUrl);
        }
      });
    }

    return () => {
      if (audioObjectUrlRef.current) {
        URL.revokeObjectURL(audioObjectUrlRef.current);
        audioObjectUrlRef.current = undefined;
      }
    };
  }, [local?.local_id]);

  const audioUrl = remote?.recording ?? local?.recording_url ?? localBlobUrl;

  // ─── Tab state ────────────────────────────────────────────────────────────
  const [tabView, setTabView] = useState<string>("transcript");

  const hasTranscription = Boolean(remote?.has_transcription);
  const shouldShowTranscriptTab =
    hasTranscription ||
    (remote?.status !== AMBIENT_SCRIBE_STATUS_UPLOADED &&
      remote?.status !== AMBIENT_SCRIBE_STATUS_TRANSCRIBED);

  const shouldShowContextTab =
    remote?.status !== AMBIENT_SCRIBE_STATUS_CANCELLED &&
    remote?.status !== AMBIENT_SCRIBE_STATUS_FAILED;

  const availableDetailTabs = useMemo(() => {
    if (!remote) return [] as string[];
    if (remote.has_transcription) {
      const tabs = ["soap"];
      if (shouldShowTranscriptTab) tabs.push("transcript");
      if (shouldShowContextTab) tabs.push("context");
      tabs.push("summary");
      if (ambientScribeProcessingEventsEnabled) tabs.push("events");
      return tabs;
    }
    if (ambientScribeProcessingEventsEnabled) {
      const tabs: string[] = [];
      if (shouldShowContextTab) tabs.push("context");
      tabs.push("events");
      return tabs;
    }
    return [];
  }, [
    remote,
    shouldShowTranscriptTab,
    shouldShowContextTab,
    ambientScribeProcessingEventsEnabled
  ]);

  const contextTabFallback = useMemo(
    () => availableDetailTabs[0] ?? "events",
    [availableDetailTabs]
  );

  const resolvedTabView = useMemo(() => {
    if (availableDetailTabs.length === 0) return tabView;
    if (availableDetailTabs.includes(tabView)) return tabView;
    return contextTabFallback;
  }, [availableDetailTabs, tabView, contextTabFallback]);

  const visibleDetailTabs = useMemo(
    () =>
      DETAIL_TAB_DISPLAY_ORDER.filter((id) => availableDetailTabs.includes(id)),
    [availableDetailTabs]
  );

  const audioRecordingsTabCount = useMemo(
    () =>
      !isAudioChunksLoading && audioChunksCount > 0
        ? audioChunksCount
        : undefined,
    [isAudioChunksLoading, audioChunksCount]
  );

  // One-shot tab initialization — does NOT re-run on every 2500ms poll refetch.
  const tabInitialized = useRef(false);
  useEffect(() => {
    tabInitialized.current = false;
  }, [remoteMedicalScribeId, local?.local_id, remote?.id]);

  useEffect(() => {
    if (tabInitialized.current || isLoading || isFetching || !remote) return;
    if (availableDetailTabs.length === 0) return;
    tabInitialized.current = true;
    setTabView(contextTabFallback);
  }, [
    remote,
    isLoading,
    isFetching,
    availableDetailTabs.length,
    contextTabFallback
  ]);

  useEffect(() => {
    if (availableDetailTabs.length === 0) return;
    if (!availableDetailTabs.includes(tabView)) {
      setTabView(contextTabFallback);
    }
  }, [availableDetailTabs, tabView, contextTabFallback]);

  // Move away from transcript tab if it disappears (e.g. streaming mode transition)
  useEffect(() => {
    if (tabView === "transcript" && !shouldShowTranscriptTab) {
      setTabView(contextTabFallback);
    }
  }, [tabView, shouldShowTranscriptTab, contextTabFallback]);

  // Sync active tab to parent (InsertScribeDialog uses this to enable Insert btn)
  useEffect(() => {
    if (mode !== "view") return;
    onTabSelected?.(resolvedTabView);
  }, [mode, resolvedTabView, onTabSelected]);

  // ─── Patient ──────────────────────────────────────────────────────────────
  const patient = useMemo(() => {
    if (remote?.patient) {
      return {
        ...remote.patient,
        fullName: getFullName({
          firstName: remote.patient?.first_name || "",
          lastName: remote.patient?.last_name || "",
          title: remote.patient?.title,
          suffix: remote.patient?.suffix
        })
      };
    }
    return local?.patient;
  }, [local?.patient, remote?.patient]);

  // // ─── Finalize stuck scribe ────────────────────────────────────────────────
  // const [finalizeStuckScribeIds, setFinalizeStuckScribeIds] = useState<
  //   Set<number>
  // >(new Set());

  // const isProcessingTooLong =
  //   remote?.status === AMBIENT_SCRIBE_STATUS_PROCESSING &&
  //   !!remote.created_at &&
  //   Date.now() - new Date(remote.created_at).getTime() > 10 * 60 * 1000;

  // Stuck in a non-terminal state with no transcription yet — pipeline is broken
  // const isStuckWithoutTranscription =
  //   !hasTranscription &&
  //   remote?.status != null &&
  //   remote.status !== AMBIENT_SCRIBE_STATUS_PROCESSING &&
  //   remote.status !== AMBIENT_SCRIBE_STATUS_COMPLETED &&
  //   remote.status !== AMBIENT_SCRIBE_STATUS_RECORDING &&
  //   remote.status !== AMBIENT_SCRIBE_STATUS_CANCELLED &&
  //   remote.status !== AMBIENT_SCRIBE_STATUS_FAILED &&
  //   remote.status !== AMBIENT_SCRIBE_STATUS_RETRYING;

  // Failed/retrying/cancelled with no transcription — mic likely captured nothing
  const isFailedOrRetryingWithoutTranscription =
    !hasTranscription &&
    (remote?.status === AMBIENT_SCRIBE_STATUS_FAILED ||
      remote?.status === AMBIENT_SCRIBE_STATUS_RETRYING ||
      remote?.status === AMBIENT_SCRIBE_STATUS_CANCELLED);

  // Failed/retrying but transcription is ready — can be finalized
  // const isFailedOrRetryingWithTranscription =
  //   hasTranscription &&
  //   (remote?.status === AMBIENT_SCRIBE_STATUS_FAILED ||
  //     remote?.status === AMBIENT_SCRIBE_STATUS_RETRYING);

  // const shouldShowFinishRecordingButton =
  //   !!remoteMedicalScribeId &&
  //   !finalizeStuckScribeIds.has(remoteMedicalScribeId) &&
  //   (isProcessingTooLong ||
  //     isStuckWithoutTranscription ||
  //     isFailedOrRetryingWithTranscription ||
  //     remote?.status === AMBIENT_SCRIBE_STATUS_RECORDING);

  // const handleFinishAudioRecording = useCallback(async () => {
  //   const scribeId = remoteMedicalScribeId;
  //   if (!scribeId) return;
  //   try {
  //     await finalizeStuckScribe({ scribe_id: scribeId }).unwrap();
  //     dispatch(setCustomMessage("Scribe finalizing in progress"));
  //     dispatch(setOpen(true));
  //     setFinalizeStuckScribeIds((prev) => new Set(prev).add(scribeId));
  //   } catch (error: any) {
  //     const message =
  //       error?.data?.message || "Failed to finalize scribe. Please try again.";
  //     dispatch(setCustomMessage(message));
  //     dispatch(setOpen(true));
  //     // Only hide the button on 400 (expected) — allow retry on network/5xx
  //     if (error?.status === 400) {
  //       setFinalizeStuckScribeIds((prev) => new Set(prev).add(scribeId));
  //     }
  //   }
  // }, [remoteMedicalScribeId, finalizeStuckScribe, dispatch]);

  // ─── Generate callbacks ───────────────────────────────────────────────────
  const checkPaywall = useCallback(async () => {
    const response = await fetchPaywallFeatures();
    const isActive = Boolean(
      response.data?.[AMBIENT_SCRIBE_FEATURE_KEY]?.active
    );
    if (!paywallBypassEnabled && !isActive) {
      dispatch(showFeatureDialog(AMBIENT_SCRIBE_FEATURE_KEY));
      return false;
    }
    return true;
  }, [fetchPaywallFeatures, paywallBypassEnabled, dispatch]);

  const handleGenerateSoap = useCallback(async () => {
    if (!(await checkPaywall())) return;
    setLocalGenerating((p) => ({ ...p, soap: true }));
    const result = await generateSoapNotes({
      id: Number(remoteMedicalScribeId)
    });
    if ("error" in result) {
      setLocalGenerating((p) => ({ ...p, soap: false }));
      dispatch(setCustomMessage(<ErrorSnackbarContent response={result} />));
      dispatch(setOpen(true));
    }
  }, [remoteMedicalScribeId, checkPaywall, generateSoapNotes, dispatch]);

  const handleGenerateSummary = useCallback(async () => {
    if (!(await checkPaywall())) return;
    setLocalGenerating((p) => ({ ...p, summary: true }));
    const result = await generateSummary({
      id: Number(remoteMedicalScribeId)
    });
    if ("error" in result) {
      setLocalGenerating((p) => ({ ...p, summary: false }));
      const err = result.error as any;
      if (err?.status === 402 && err?.data?.subscription_state) {
        setSummaryGenerateError({
          kind: "subscription",
          error: err.data.error,
          message: err.data.message
        });
      } else {
        setSummaryGenerateError({ kind: "generic" });
      }
    }
  }, [remoteMedicalScribeId, checkPaywall, generateSummary]);

  const handleGenerateTranscript = useCallback(async () => {
    if (!(await checkPaywall())) return;
    setLocalGenerating((p) => ({ ...p, transcript: true }));
    const result = await generateTranscription({
      id: Number(remoteMedicalScribeId)
    });
    if ("error" in result) {
      setLocalGenerating((p) => ({ ...p, transcript: false }));
      dispatch(setCustomMessage(<ErrorSnackbarContent response={result} />));
      dispatch(setOpen(true));
    }
  }, [remoteMedicalScribeId, checkPaywall, generateTranscription, dispatch]);

  // Called by GenerateScribeSection after firing the async mutation so the
  // parent can set localGenerating.soap = true without waiting for the next poll.
  const handleSoapGenerateStarted = useCallback(() => {
    setLocalGenerating((p) => ({ ...p, soap: true }));
  }, []);

  // ─── Derived values ───────────────────────────────────────────────────────
  const createdAt = remote?.created_at ?? local?.created_at;
  const duration = remote?.duration ?? local?.duration;

  const showAudioPlayerSection =
    recordingState !== RECORDING_STATE_IN_PROGRESS &&
    (audioUrl ||
      Boolean(local?.local_id || remote?.id) ||
      showPausedScribeActionButtons);

  // ─── Render ───────────────────────────────────────────────────────────────
  const isEditMode = mode === "edit" && showViewEditTabs;

  return (
    <Stack
      flex={6}
      sx={{
        boxSizing: "border-box",
        flex: 1,
        minHeight: 0,
        // overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        ...(showViewEditTabs ? {} : { mt: 1.75 }),
        ...sx
      }}
    >
      {showViewEditTabs && (
        <TabContext value={mode}>
          <TabList
            onChange={handleTopTabChange}
            sx={{ ...CHART_NOTES_STYLE_TAB_LIST_SX, mb: 1, flexShrink: 0 }}
          >
            <Tab
              value="view"
              label={
                <ChartNotesStyleTabLabel
                  label="View Scribe"
                  isActive={mode === "view"}
                />
              }
            />
            <Tab
              value="edit"
              label={
                <ChartNotesStyleTabLabel
                  label="Audio Recordings"
                  isActive={mode === "edit"}
                  count={audioRecordingsTabCount}
                />
              }
            />
          </TabList>
        </TabContext>
      )}

      {isEditMode ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <EditScribeTab
            key={`${remoteMedicalScribeId}-${editBottomTab}`}
            scribeId={Number(remoteMedicalScribeId)}
            initialBottomTab={editBottomTab}
            onClose={onEditClose ?? (() => {})}
            onFinalized={handleEditFinalized}
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {!hideHeader && (
            <Stack sx={showViewEditTabs ? { pt: 2, flexShrink: 0 } : undefined}>
              <ScribeMetadataHeader
                createdAt={createdAt}
                patient={patient}
                status={remote?.status ?? local?.status}
                duration={duration}
                scribeId={remote?.id}
              />

              {isPausedStatus && !isPausedCompleting && (
                <PausedStatusWarningAlert />
              )}

              {showPausedScribeActionButtons && !showAudioPlayerSection && (
                <Box sx={{ mb: 1 }}>
                  <PausedScribeActionButtons
                    isPaused={isPausedStatus}
                    isCompleted={isCompletedStatus}
                    isRecordingStatus={isRecordingStatus}
                    isProcessingStatus={isProcessingStatus}
                    canCompleteFromFailedOrCancelled={
                      canCompleteFromFailedOrCancelled
                    }
                    isFailedOrCancelledAwaitingChunks={
                      isFailedOrCancelledAwaitingChunks
                    }
                    isWidgetActive={isPausedWidgetActive}
                    isLocallyPausedWidget={isPausedLocallyPausedWidget}
                    isResuming={isPausedResuming}
                    isResumePleaseWait={isPausedResumePleaseWait}
                    isCompleting={isPausedCompleting}
                    onResume={handlePausedResume}
                    onComplete={handlePausedComplete}
                  />
                </Box>
              )}

              {showAudioPlayerSection && (
                <ScribeAudioPlayer
                  audioUrl={audioUrl}
                  remoteMedicalScribeId={remoteMedicalScribeId}
                  localId={local?.local_id}
                  remoteRecordingUrl={remote?.recording}
                  disabled={recordingState === RECORDING_STATE_IN_PROGRESS}
                  trailingActions={
                    showPausedScribeActionButtons ? (
                      <PausedScribeActionButtons
                        isPaused={isPausedStatus}
                        isCompleted={isCompletedStatus}
                        isRecordingStatus={isRecordingStatus}
                        isProcessingStatus={isProcessingStatus}
                        canCompleteFromFailedOrCancelled={
                          canCompleteFromFailedOrCancelled
                        }
                        isFailedOrCancelledAwaitingChunks={
                          isFailedOrCancelledAwaitingChunks
                        }
                        isWidgetActive={isPausedWidgetActive}
                        isLocallyPausedWidget={isPausedLocallyPausedWidget}
                        isResuming={isPausedResuming}
                        isResumePleaseWait={isPausedResumePleaseWait}
                        isCompleting={isPausedCompleting}
                        onResume={handlePausedResume}
                        onComplete={handlePausedComplete}
                      />
                    ) : undefined
                  }
                />
              )}
            </Stack>
          )}

          {isFailedOrRetryingWithoutTranscription && (
            <Alert severity="warning" sx={{ mt: 3, flexShrink: 0 }}>
              No audio was captured for this recording. Check that your
              microphone is connected and the correct device is selected, then
              start a new recording.
            </Alert>
          )}

          {isLoading && !remote && (
            <Stack alignItems="center" justifyContent="center" py={6}>
              <CircularProgress />
            </Stack>
          )}

          {remote?.status !== AMBIENT_SCRIBE_STATUS_RECORDING && remote && (
            <>
              {visibleDetailTabs.length > 0 && (
                <TabContext value={resolvedTabView}>
                  <TabList
                    onChange={(_, value) => setTabView(value)}
                    sx={{
                      ...CHART_NOTES_STYLE_TAB_LIST_SX,
                      ...CHART_NOTES_STYLE_TAB_LIST_SCROLL_SX,
                      mt: 2,
                      flexShrink: 0
                    }}
                  >
                    {visibleDetailTabs.map((tabId) => (
                      <Tab
                        key={tabId}
                        value={tabId}
                        label={
                          <ChartNotesStyleTabLabel
                            label={getDetailTabLabel(
                              tabId,
                              Boolean(remote.has_transcription)
                            )}
                            isActive={resolvedTabView === tabId}
                          />
                        }
                      />
                    ))}
                  </TabList>
                </TabContext>
              )}

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden"
                }}
              >
                {shouldShowContextTab && Boolean(remote.id) && (
                  <Box
                    sx={{
                      display: resolvedTabView === "context" ? "flex" : "none",
                      flex: 1,
                      minHeight: 0,
                      flexDirection: "column",
                      overflow: "hidden"
                    }}
                  >
                    <ScribeContextEditor
                      scribeId={Number(remote.id)}
                      customContext={remote.custom_context}
                      patientId={remote.patient?.id}
                      userId={userId}
                      isActive={resolvedTabView === "context"}
                      onFlushSaveRef={flushContextSaveRef}
                    />
                  </Box>
                )}

                {resolvedTabView === "transcript" && (
                  <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                    <TranscriptTab
                      id={Number(remote.id)}
                      transcription={remote.transcription}
                      duration={duration}
                      status={remote.status}
                      isGeneratingTranscription={isGeneratingTranscript}
                      onTranscribe={handleGenerateTranscript}
                    />
                  </Box>
                )}

                {remote.has_transcription &&
                  Boolean(remote.id) &&
                  resolvedTabView === "summary" && (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                      <TextTab
                        id={Number(remote.id)}
                        value={String(remote.summary ?? "")}
                        type="summary"
                        isLoading={isLoading || isFetching}
                        isGenerating={isGeneratingSummary}
                        onGenerate={handleGenerateSummary}
                      />
                    </Box>
                  )}

                {remote.has_transcription &&
                  Boolean(remote.id) &&
                  resolvedTabView === "soap" && (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                      <TextTab
                        id={Number(remote.id)}
                        value={String(remote.soap_notes ?? "")}
                        type="soap"
                        isLoading={isLoading || isFetching}
                        isGenerating={isGeneratingSoap}
                        onGenerate={handleGenerateSoap}
                        onGenerateStarted={handleSoapGenerateStarted}
                        scribeTemplateId={remote.ambient_scribe_template_id}
                      />
                    </Box>
                  )}

                {ambientScribeProcessingEventsEnabled &&
                  resolvedTabView === "events" &&
                  Boolean(remote.id) && (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                      <ProcessingEventsTimeline
                        key={remote.id}
                        scribeId={remote.id}
                      />
                    </Box>
                  )}
              </Box>
            </>
          )}
        </Box>
      )}

      <MedicalScribeFeedbackDialog />

      <MuiDialog
        open={summaryGenerateError !== null}
        onClose={() => setSummaryGenerateError(null)}
        sx={{ zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar) }}
      >
        <DialogTitle>
          {summaryGenerateError?.kind === "subscription"
            ? "Subscription Required"
            : "Generation Failed"}
        </DialogTitle>
        <DialogContent>
          {summaryGenerateError?.kind === "subscription" ? (
            <Alert severity="warning">
              <AlertTitle>{summaryGenerateError.error}</AlertTitle>
              {summaryGenerateError.message}
            </Alert>
          ) : (
            <Typography>
              Something went wrong while processing your request. Please try
              again in a few moments. If the issue persists, contact Suno
              support for assistance.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryGenerateError(null)}>Close</Button>
        </DialogActions>
      </MuiDialog>
    </Stack>
  );
}
